import type { User } from '../types/types'

type ServiceRedirectPaths = 'survey' | 'jidoka' | 'kestrixhome'
type ServiceRedirectTypes = 'survey' | 'jidoka' | 'home'

// Map service paths to their actual URL path names
const SERVICE_PATH_MAP: Record<ServiceRedirectTypes, ServiceRedirectPaths> = {
  survey: 'survey',
  jidoka: 'jidoka',
  home: 'kestrixhome'
}

const SERVICE_PATHS: Record<User['role'], ServiceRedirectTypes> = {
  SUPERADMIN: 'jidoka',
  JIDOKEE: 'jidoka',
  ANNOTATOR: 'jidoka',
  THERMOGRAPHER: 'jidoka',
  QC_ANNOTATOR: 'jidoka',
  ORGADMIN: 'survey',
  ORGMEMBER: 'survey',
  RESIDENT: 'home'
}

/**
 * Whether the login app is being served from a developer machine.
 * Matches on hostname, so it still holds when the dev server takes another port.
 */
export const isLocalDevelopment = (hostname: string): boolean =>
  hostname === 'localhost' || hostname === '127.0.0.1'

/** The apps a SUPERADMIN can choose between after logging in. */
export const APP_CHOICES = [
  { path: '/jidoka', label: 'Jidoka' },
  { path: '/survey', label: 'Survey' },
  { path: '/rapid', label: 'Rapid' }
] as const

export type AppChoicePath = (typeof APP_CHOICES)[number]['path']

/**
 * Whether the user picks their app instead of following the role-based redirect.
 * Only a SUPERADMIN picks. Locally nothing redirects, so nothing to pick.
 */
export const shouldChooseApp = (
  role: User['role'],
  hostname: string
): boolean => role === 'SUPERADMIN' && !isLocalDevelopment(hostname)

/**
 * Navigates to the app the user chose, on the current origin.
 */
export const navigateToApp = (path: AppChoicePath): void => {
  window.location.assign(`${window.location.origin}${path}`)
}

/**
 * Utility to determine the service path based on the user role
 * Returns a full URL with protocol, hostname, and /v2/ prefix
 */
export const getRedirectBasedOnRole = (userRole: User['role']): string => {
  const servicePath = SERVICE_PATHS[userRole]
  const redirectPath = SERVICE_PATH_MAP[servicePath]

  const redirectUrl = `${window.location.origin}/${redirectPath}`
  return redirectUrl
}

/**
 * Utility to get the previous page URL from the session storage
 * @returns The previous page URL if it exists, false otherwise
 */
const getPreviousPage = (): URL | false => {
  const returnPath = window.sessionStorage.getItem('returnUrl')

  if (!returnPath) {
    return false
  }

  // clean up storage to avoid strange routing behaviour
  window.sessionStorage.removeItem('returnUrl')

  const returnUrl = new URL(returnPath)

  if (returnUrl.origin !== window.location.origin) {
    return false
  }

  return returnUrl
}

/**
 * Utility to navigate to the service path based on
 * the user role or where they came from
 * Preference is given to the previous location
 * Assumption is that the user will navigate to this path
 * from a previous page, so we will mostly navigate back there
 * But if not, we will navigate to the service path based on the user role
 */
export const navigateToService = (user: User): void => {
  // If working locally, don't redirect
  if (isLocalDevelopment(window.location.hostname)) {
    console.info(
      'If working on another service, ensure ports are aligned, close this app, and open the service you are working on'
    )
    return
  }

  const previousPage = getPreviousPage()

  // Priority given to the previous page
  if (previousPage) {
    window.location.assign(previousPage)
    return
  }
  const redirectPath = getRedirectBasedOnRole(user.role)
  window.location.assign(redirectPath)
  return
}
